import { useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import '@erp/i18n';
import { createSupabaseServices } from '@erp/data-access/supabase-adapter';
import { queryClient } from '@/lib/query-client';
import {
  proQueryPersister,
  proPersistedCacheBuster,
  shouldPersistProQuery,
} from '@/lib/query-persist';
import { ServiceProvider } from '@/lib/service-context';
import { ProAuthProvider, useProAuth } from '@/lib/auth-context';
import { ProBrandingProvider } from '@/lib/pro-branding';
import { CartProvider } from '@/lib/cart-context';
import { FlyToCartProvider } from '@/lib/fly-to-cart';

import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { UpdatePasswordPage } from '@/pages/auth/UpdatePasswordPage';
import { PendingApprovalPage } from '@/pages/auth/PendingApprovalPage';

import { ElectricianLayout } from '@/components/layout/ElectricianLayout';
import { ClientLayout } from '@/components/layout/ClientLayout';
import { RequireApprovedPro } from '@/components/layout/RequireApprovedPro';
import { CLIENT_MODE_ROUTES } from '@/lib/client-mode';

import { HomePage } from '@/pages/HomePage';
import { LandingPage } from '@/pages/LandingPage';
import { DevisListPage } from '@/pages/DevisListPage';
import { DevisDetailPage } from '@/pages/DevisDetailPage';
import { OrdresListPage } from '@/pages/OrdresListPage';
import { OrdreDetailPage } from '@/pages/OrdreDetailPage';
import { ClientsPage } from '@/pages/ClientsPage';
import { ClientProfilePage } from '@/pages/ClientProfilePage';
import { PortefeuillePage } from '@/pages/PortefeuillePage';
import { DashboardPage } from '@/pages/DashboardPage';
import { StatistiquePage } from '@/pages/StatistiquePage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { CataloguePage } from '@/pages/CataloguePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { FormationPage } from '@/pages/FormationPage';
import { NotificationsPreviewPage } from '@/pages/NotificationsPreviewPage';

function CartShell({ children }: { children: React.ReactNode }) {
  const { proCustomer } = useProAuth();
  return (
    <FlyToCartProvider>
      <CartProvider
        commissionRate={proCustomer?.commissionRate ?? 0}
        defaultTier={proCustomer?.defaultRemiseTier ?? 'moy'}
      >
        {children}
      </CartProvider>
    </FlyToCartProvider>
  );
}

// Force-redirects users in PASSWORD_RECOVERY state to /auth/update-password
// so they can't end up on /login or /home with a half-baked recovery
// session. Lives inside <BrowserRouter> so it can use react-router hooks.
function PasswordRecoveryRedirect() {
  const { isPasswordRecovery } = useProAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isPasswordRecovery && location.pathname !== '/auth/update-password') {
      navigate('/auth/update-password', { replace: true });
    }
  }, [isPasswordRecovery, location.pathname, navigate]);
  return null;
}

function AppRoutes() {
  const { user, isLoading } = useProAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--pro-bg)' }}>
        <div className="animate-pulse text-neutral-400">…</div>
      </div>
    );
  }

  return (
    // CartShell est monté UNE fois au-dessus des routes : les coquilles
    // électricien et client partagent alors le même CartProvider en mémoire.
    // Monté par coquille, passer en mode client démonterait le provider et
    // refermerait le tiroir du panier au moment de le montrer au client.
    <CartShell>
      <PasswordRecoveryRedirect />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/tableau-de-bord" replace /> : <LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={user ? <Navigate to="/tableau-de-bord" replace /> : <LoginPage />} />
        <Route path="/register" element={user ? <Navigate to="/tableau-de-bord" replace /> : <RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/update-password" element={<UpdatePasswordPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
        {/* Planche de référence des notifications, hors authentification en DEV
            seulement : elle ne monte que des données factices, donc elle se
            revoit sans session. En production elle reste derrière le portillon
            (route plus bas). */}
        {import.meta.env.DEV && (
          <Route
            path="/notifications-apercu-dev"
            element={<NotificationsPreviewPage />}
          />
        )}

      <Route
        element={
          <RequireApprovedPro>
            <ElectricianLayout />
          </RequireApprovedPro>
        }
      >
        <Route path="/home" element={<HomePage />} />
        <Route path="/devis" element={<DevisListPage />} />
        <Route path="/devis/:code" element={<DevisDetailPage />} />
        <Route path="/ordres" element={<OrdresListPage />} />
        <Route path="/ordres/:code" element={<OrdreDetailPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientProfilePage />} />
        <Route path="/portefeuille" element={<PortefeuillePage />} />
        <Route path="/tableau-de-bord" element={<DashboardPage />} />
        <Route path="/statistique" element={<StatistiquePage />} />
        <Route path="/catalogue" element={<CataloguePage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/formation" element={<FormationPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* Planche de référence des notifications — non listée dans le menu,
            elle sert à revoir toutes les variantes d'un coup d'œil. */}
        <Route path="/notifications-apercu" element={<NotificationsPreviewPage />} />
      </Route>

      {/* Mode client — mêmes pages, coquille dépouillée. */}
      <Route
        element={
          <RequireApprovedPro>
            <ClientLayout />
          </RequireApprovedPro>
        }
      >
        <Route path={CLIENT_MODE_ROUTES.shop} element={<HomePage />} />
        <Route
          path={CLIENT_MODE_ROUTES.catalogue}
          element={<CataloguePage />}
        />
        <Route path={CLIENT_MODE_ROUTES.checkout} element={<CheckoutPage />} />
      </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </CartShell>
  );
}

function AppShell() {
  const masterUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const masterAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

  const services = useMemo(
    () => createSupabaseServices({ masterUrl, masterAnonKey }),
    [masterUrl, masterAnonKey],
  );

  return (
    <ServiceProvider
      services={{
        dataService: services.dataService,
        authService: services.authService,
        storageService: services.storageService,
      }}
    >
      <ProBrandingProvider>
        <ProAuthProvider authService={services.authService} dataService={services.dataService}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ProAuthProvider>
      </ProBrandingProvider>
    </ServiceProvider>
  );
}

export function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: proQueryPersister,
        // Tenant-scoped: a blob from another tenant is discarded on restore
        // rather than hydrated into this session.
        buster: proPersistedCacheBuster(),
        // Drop entries older than 24h on hydration.
        maxAge: 24 * 60 * 60 * 1000,
        // Only the tenant-shared catalogue is written to disk — never
        // user-scoped data (favourites, wallet, notifications).
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistProQuery },
      }}
    >
      <AppShell />
    </PersistQueryClientProvider>
  );
}
